"use client";

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { usePathname, useRouter } from "next/navigation";
import type { AktiverTestlauf } from "@/dev-testbed/testlauf/typen.ts";
import { designClass } from "@/dev-testbed/ui/design.ts";
import { GesperrtOverlay } from "./GesperrtOverlay.tsx";
import { pfadInAllowlist } from "./glob.ts";
import {
  TestScopeContext,
  type AbschlussMeldung,
  type TestScopeKontext,
} from "./test-scope-context.ts";

const POLL_MS = 30_000;
const TOAST_MS = 2_500;
const HINT_COOKIE = "dev_scope_aktiv";

type HistoryFn = typeof history.pushState;

function hatHinweisCookie(): boolean {
  if (typeof document === "undefined") return false;
  return document.cookie
    .split(";")
    .map((p) => p.trim())
    .some((p) => p === `${HINT_COOKIE}=1`);
}

function loescheHinweisCookie(): void {
  document.cookie = `${HINT_COOKIE}=; Path=/; Max-Age=0; SameSite=Lax`;
}

function normalisierePfad(raw: string): string {
  try {
    if (raw.startsWith("http://") || raw.startsWith("https://")) {
      const u = new URL(raw);
      return u.pathname || "/";
    }
  } catch {
    /* relative */
  }
  const ohneQuery = raw.split("?")[0]?.split("#")[0] ?? raw;
  if (!ohneQuery) return "/";
  return ohneQuery.startsWith("/") ? ohneQuery : `/${ohneQuery}`;
}

function istInternerPfad(href: string): boolean {
  if (!href || href.startsWith("#")) return false;
  if (href.startsWith("mailto:") || href.startsWith("tel:")) return false;
  if (href.startsWith("//")) return false;
  try {
    if (href.startsWith("http://") || href.startsWith("https://")) {
      return new URL(href).origin === window.location.origin;
    }
  } catch {
    return false;
  }
  return href.startsWith("/");
}

function bereichLabelAusPfad(pfad: string): string {
  const segmente = pfad.split("/").filter(Boolean);
  if (segmente.length === 0) return "Start";
  const bekannt: Record<string, string> = {
    dashboard: "Dashboard",
    strategy: "Strategie",
    execution: "To Do's",
    outcomes: "Reports",
    company: "Unternehmen",
    data: "Daten",
    agent: "Agent",
    developer: "Developer",
    settings: "Einstellungen",
    profile: "Profil",
    notifications: "Inbox",
    register: "Registrierung",
    login: "Login",
  };
  for (let i = segmente.length - 1; i >= 0; i -= 1) {
    const s = segmente[i]!;
    if (bekannt[s]) return bekannt[s];
  }
  return segmente[segmente.length - 1] ?? pfad;
}

type LadeErgebnis =
  | { art: "passiv" }
  | { art: "auth_weg" }
  | { art: "kein_scope" }
  | { art: "scope"; lauf: AktiverTestlauf };

async function ladeAktivenLauf(): Promise<LadeErgebnis> {
  if (!hatHinweisCookie()) return { art: "passiv" };

  try {
    const res = await fetch("/api/dev/testlauf/aktiv", {
      method: "GET",
      credentials: "same-origin",
      cache: "no-store",
    });
    if (res.status === 401 || res.status === 404) {
      loescheHinweisCookie();
      return { art: "auth_weg" };
    }
    if (!res.ok) return { art: "kein_scope" };
    const data: unknown = await res.json();
    if (data === null) return { art: "kein_scope" };
    if (!data || typeof data !== "object") return { art: "kein_scope" };
    return { art: "scope", lauf: data as AktiverTestlauf };
  } catch {
    return { art: "kein_scope" };
  }
}

export function TestScopeProvider({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const router = useRouter();
  const [lauf, setLauf] = useState<AktiverTestlauf | null>(null);
  const [overlayPfad, setOverlayPfad] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [letzterAbschluss, setLetzterAbschluss] = useState<AbschlussMeldung | null>(
    null,
  );
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const laufRef = useRef(lauf);
  laufRef.current = lauf;
  const lastPathRef = useRef<string | null>(null);

  const [polling, setPolling] = useState(false);

  const refresh = useCallback(async () => {
    const ergebnis = await ladeAktivenLauf();
    if (ergebnis.art === "passiv" || ergebnis.art === "auth_weg") {
      setLauf(null);
      setPolling(false);
      return;
    }
    setLauf(ergebnis.art === "scope" ? ergebnis.lauf : null);
    setPolling(true);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh, pathname]);

  useEffect(() => {
    if (!polling) return;
    const id = window.setInterval(() => {
      void refresh();
    }, POLL_MS);
    return () => window.clearInterval(id);
  }, [refresh, polling]);

  const zeigeToast = useCallback((text: string) => {
    setToast(text);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), TOAST_MS);
  }, []);

  const meldeEreignis = useCallback((art: string, wert: string) => {
    void fetch("/api/dev/testlauf/ereignis", {
      method: "POST",
      credentials: "same-origin",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ art, wert }),
    })
      .then(async (res) => {
        if (!res.ok) return;
        const data: unknown = await res.json().catch(() => null);
        if (!data || typeof data !== "object") return;
        const o = data as Record<string, unknown>;
        if (o.abschluss === "erfolgreich" || o.abschluss === "fehlgeschlagen") {
          const meldung: AbschlussMeldung = {
            abschluss: o.abschluss,
            fehlergrund: typeof o.fehlergrund === "string" ? o.fehlergrund : null,
            dauerMs: typeof o.dauerMs === "number" ? o.dauerMs : null,
            costUsd: typeof o.costUsd === "number" ? o.costUsd : null,
          };
          setLetzterAbschluss(meldung);
          setLauf(null);
          setPolling(false);
          loescheHinweisCookie();
          window.dispatchEvent(
            new CustomEvent("dev-testbed-abschluss", { detail: meldung }),
          );
        }
      })
      .catch(() => {
        /* Logging darf die UI nicht blockieren */
      });
  }, []);

  // Pathname-Wechsel → Navigations-Ereignis (A7)
  useEffect(() => {
    if (!lauf) {
      lastPathRef.current = pathname;
      return;
    }
    if (lastPathRef.current === pathname) return;
    lastPathRef.current = pathname;
    meldeEreignis("navigation", pathname);
  }, [lauf, meldeEreignis, pathname]);

  const routeErlaubt = useCallback(
    (pfad: string): boolean => {
      const current = laufRef.current;
      if (!current) return true;
      if (current.beobachtungsmodus) return true;
      return pfadInAllowlist(normalisierePfad(pfad), current.erlaubteRouten);
    },
    [],
  );

  const aktionErlaubt = useCallback((id: string): boolean => {
    const current = laufRef.current;
    if (!current) return true;
    if (current.beobachtungsmodus) return true;
    return current.erlaubteAktionen.includes(id);
  }, []);

  const vorgabeFuer = useCallback((feld: string) => {
    const current = laufRef.current;
    if (!current) return null;
    return current.vorgaben.find((v) => v.feld === feld) ?? null;
  }, []);

  const oeffneOverlay = useCallback(
    (pfad: string) => {
      meldeEreignis("navigation", pfad);
      setOverlayPfad(pfad);
    },
    [meldeEreignis],
  );

  const einstiegRoute = lauf?.einstieg?.route ?? "/dashboard/developer";

  const onZurueck = useCallback(() => {
    meldeEreignis("gesperrt", "zurueck_zum_test");
    setOverlayPfad(null);
    router.push(einstiegRoute);
  }, [einstiegRoute, meldeEreignis, router]);

  const onTrotzdem = useCallback(async () => {
    const ziel = overlayPfad;
    meldeEreignis("gesperrt", "trotzdem_oeffnen");
    try {
      await fetch("/api/dev/testlauf/beenden", {
        method: "POST",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ ergebnis: "abgebrochen" }),
      });
    } catch {
      /* trotzdem navigieren */
    }
    setOverlayPfad(null);
    setLauf(null);
    setPolling(false);
    if (ziel) router.push(ziel);
  }, [meldeEreignis, overlayPfad, router]);

  // Navigations-Sperre: Link-Klicks (Capture)
  useEffect(() => {
    function onClick(ev: MouseEvent) {
      const current = laufRef.current;
      if (!current) return;
      if (ev.defaultPrevented) return;
      if (ev.button !== 0) return;
      if (ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey) return;

      const target = ev.target;
      if (!(target instanceof Element)) return;
      const anker = target.closest("a");
      if (!anker) return;
      const href = anker.getAttribute("href");
      if (!href || !istInternerPfad(href)) return;

      const pfad = normalisierePfad(href);

      if (current.beobachtungsmodus) {
        meldeEreignis("navigation", pfad);
        return;
      }

      if (pfadInAllowlist(pfad, current.erlaubteRouten)) return;

      ev.preventDefault();
      ev.stopPropagation();
      oeffneOverlay(pfad);
    }

    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [meldeEreignis, oeffneOverlay]);

  // Programmatische Navigation
  useEffect(() => {
    const origPush: HistoryFn = history.pushState.bind(history);
    const origReplace: HistoryFn = history.replaceState.bind(history);

    function pruefeHistory(
      original: HistoryFn,
      data: unknown,
      unused: string,
      url?: string | URL | null,
    ): void {
      const current = laufRef.current;
      if (!current || url == null || url === "") {
        original.call(history, data, unused, url);
        return;
      }
      const pfad = normalisierePfad(String(url));
      if (current.beobachtungsmodus) {
        meldeEreignis("navigation", pfad);
        original.call(history, data, unused, url);
        return;
      }
      if (pfadInAllowlist(pfad, current.erlaubteRouten)) {
        original.call(history, data, unused, url);
        return;
      }
      oeffneOverlay(pfad);
    }

    history.pushState = ((data, unused, url) => {
      pruefeHistory(origPush, data, unused, url);
    }) as HistoryFn;

    history.replaceState = ((data, unused, url) => {
      pruefeHistory(origReplace, data, unused, url);
    }) as HistoryFn;

    return () => {
      history.pushState = origPush;
      history.replaceState = origReplace;
    };
  }, [meldeEreignis, oeffneOverlay]);

  // Globaler Aktions-Fallback über data-test-aktion
  useEffect(() => {
    function onClick(ev: MouseEvent) {
      const current = laufRef.current;
      if (!current || current.beobachtungsmodus) return;
      const target = ev.target;
      if (!(target instanceof Element)) return;
      const el = target.closest("[data-test-aktion]");
      if (!el) return;
      const id = el.getAttribute("data-test-aktion");
      if (!id) return;
      if (current.erlaubteAktionen.includes(id)) return;
      ev.preventDefault();
      ev.stopPropagation();
      meldeEreignis("aktion", id);
      zeigeToast("Nicht Teil dieses Tests");
    }

    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [meldeEreignis, zeigeToast]);

  const kontext: TestScopeKontext = useMemo(
    () => ({
      aktiv: lauf !== null,
      beobachtungsmodus: lauf?.beobachtungsmodus ?? false,
      regeln: lauf,
      routeErlaubt,
      aktionErlaubt,
      vorgabeFuer,
      meldeEreignis,
      letzterAbschluss,
    }),
    [lauf, letzterAbschluss, routeErlaubt, aktionErlaubt, vorgabeFuer, meldeEreignis],
  );

  return (
    <TestScopeContext.Provider value={kontext}>
      {children}
      <GesperrtOverlay
        open={overlayPfad !== null && lauf !== null}
        prozessTitel={lauf?.prozessTitel ?? ""}
        schrittTitel={lauf?.schrittTitel ?? ""}
        schrittNummer={lauf?.schrittNummer ?? 0}
        bereichLabel={overlayPfad ? bereichLabelAusPfad(overlayPfad) : ""}
        onZurueck={onZurueck}
        onTrotzdem={() => {
          void onTrotzdem();
        }}
      />
      {toast ? (
        <div
          className={`fixed bottom-6 left-1/2 z-[90] -translate-x-1/2 border border-[var(--border-default)] bg-[var(--surface-app)] px-4 py-2 shadow-md ${designClass.karte} ${designClass.fliesstext} text-[var(--text-primary)]`}
          role="status"
        >
          {toast}
        </div>
      ) : null}
    </TestScopeContext.Provider>
  );
}
